<template>
    <!--
      Presentational modal shell: overlay + elevated white box + entrance/exit
      motion + a11y (role/dialog, Escape, backdrop dismiss). Owns NO business
      logic — callers compose content through slots. Fully self-contained: it
      inlines its own colors (does not depend on any ancestor's CSS custom
      properties) and its fixed overlay sits at z-index 2000 above the page.

      Slots:
        - header  : replaces the default title (optional)
        - default : body content (caller owns its padding)
        - footer  : pinned actions (optional)
    -->
    <transition name="baseModal">
        <div v-if="show" class="baseModal" @click.self="OnBackdrop">
            <div
                class="baseModal__box"
                :style="boxStyle"
                role="dialog"
                aria-modal="true"
                :aria-label="ariaLabel || null"
                :aria-labelledby="hasTitle ? titleId : null"
            >
                <div v-if="HasHeader()" class="baseModal__header">
                    <slot name="header">
                        <span :id="titleId" class="baseModal__title">{{ title }}</span>
                    </slot>
                    <button
                        v-if="closable"
                        type="button"
                        class="baseModal__close"
                        aria-label="Cerrar"
                        @click="Close"
                    >&#x2715;</button>
                </div>

                <slot></slot>

                <div v-if="HasFooter()" class="baseModal__footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'BaseModal',

    props: {
        // v-model:show
        show: {
            type: Boolean,
            default: false
        },
        // When set, renders the default header with this title.
        title: {
            type: String,
            default: ''
        },
        // Show the × close button (and allow Escape to close).
        closable: {
            type: Boolean,
            default: true
        },
        // Allow closing via backdrop click / Escape. Set false for blocking modals.
        dismissible: {
            type: Boolean,
            default: true
        },
        // Box max width in px (dynamic → CSS custom property, per no-inline-styles rule).
        maxWidth: {
            type: Number,
            default: 440
        },
        // Optional fixed box height (CSS length, e.g. '90vh'). Empty = auto-fit to
        // content (capped by the CSS max-height). Lets a caller keep the box a
        // constant size across content changes (e.g. navigating records).
        boxHeight: {
            type: String,
            default: ''
        },
        // Accessible name for header-less modals (the setup modal owns its heading).
        ariaLabel: {
            type: String,
            default: ''
        }
    },

    emits: ['update:show', 'close'],

    data(){
        return {
            titleId: 'baseModalTitle-' + Math.random().toString(36).slice(2)
        }
    },

    computed: {
        boxStyle(){
            const style = { '--baseModal-max-width': this.maxWidth + 'px' };
            if(this.boxHeight){
                style.height = this.boxHeight;
            }
            return style;
        },
        hasTitle(){
            return this.title !== '';
        }
    },

    mounted(){
        document.addEventListener('keydown', this.OnKeydown);
    },

    unmounted(){
        document.removeEventListener('keydown', this.OnKeydown);
    },

    methods: {
        // Methods, not computed: $slots isn't reactive in Vue 3, so a computed
        // reading it caches stale (callers that conditionally pass #header/#footer,
        // e.g. FileModal's edit-mode footer, would get stuck on the first result).
        HasHeader(){
            return this.hasTitle || !!this.$slots.header || this.closable;
        },

        HasFooter(){
            return !!this.$slots.footer;
        },

        Close(){
            this.$emit('update:show', false);
            this.$emit('close');
        },

        OnBackdrop(){
            if(this.dismissible){
                this.Close();
            }
        },

        OnKeydown(e){
            if(e.key === 'Escape' && this.show && this.dismissible){
                this.Close();
            }
        }
    }
}
</script>

<style scoped>
/* Values are inlined (not brand CSS vars) because this component teleports to
   <body> and cannot inherit any ancestor's custom properties. */
.baseModal {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(23, 23, 31, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.baseModal__box {
    width: 100%;
    max-width: var(--baseModal-max-width, 440px);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    /* Keep the box on its own compositor layer permanently. Without this the
       layer is destroyed when the entrance transform resolves to none, forcing
       the text to re-rasterize on the integer pixel grid — a visible 1px jump. */
    transform: translateZ(0);
    backface-visibility: hidden;
}

.baseModal__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 24px 6px;
}

.baseModal__title {
    font-family: 'SwitzerBold', sans-serif;
    font-size: 16px;
    letter-spacing: -0.01em;
    color: #1f1f2c;
}

.baseModal__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: transparent;
    border: none;
    border-radius: 10px;
    font-size: 17px;
    color: #6b6680;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    transition: background-color 160ms ease, color 160ms ease;
}

/* Matches TaskCategoriesModal's .tcm-iconBtn hover treatment (lavender-soft
   fill + accent-ink glyph) — gated to real pointers so touch taps don't get a
   sticky hover state. */
@media (hover: hover) and (pointer: fine) {
    .baseModal__close:hover {
        background-color: #f2f4ff;
        color: #4b4770;
    }
}

.baseModal__footer {
    flex-shrink: 0;
}

/* Entrance/exit: overlay fades, box scales in from slightly below. */
.baseModal-enter-active {
    transition: opacity 300ms cubic-bezier(0.23, 1, 0.32, 1);
}

.baseModal-leave-active {
    transition: opacity 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.baseModal-enter-active .baseModal__box {
    transition: transform 340ms cubic-bezier(0.23, 1, 0.32, 1), opacity 340ms cubic-bezier(0.23, 1, 0.32, 1);
}

.baseModal-leave-active .baseModal__box {
    transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1), opacity 200ms cubic-bezier(0.23, 1, 0.32, 1);
}

.baseModal-enter-from,
.baseModal-leave-to {
    opacity: 0;
}

.baseModal-enter-from .baseModal__box,
.baseModal-leave-to .baseModal__box {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
    .baseModal-enter-from .baseModal__box,
    .baseModal-leave-to .baseModal__box {
        transform: none;
    }
}
</style>
